<template>
  <Transition name="fade">
    <div v-if="loading" class="preloader">
      <!-- Background Shapes -->
      <div class="bg-shapes">
        <!-- Floating circles -->
        <div class="shape circle c1"></div>
        <div class="shape circle c2"></div>
        <div class="shape circle c3"></div>
        <div class="shape circle c4"></div>
        <!-- Floating squares -->
        <div class="shape square s1"></div>
        <div class="shape square s2"></div>
        <!-- Floating triangles -->
        <div class="shape triangle t1"></div>
        <div class="shape triangle t2"></div>
      </div>

      <!-- Main Content -->
      <div class="loader-main">
        <!-- Orbiting Rings -->
        <div class="orbit-container">
          <div class="orbit orbit-1">
            <div class="orbit-dot"></div>
          </div>
          <div class="orbit orbit-2">
            <div class="orbit-dot"></div>
          </div>
          <div class="orbit orbit-3">
            <div class="orbit-dot"></div>
          </div>
        </div>

        <!-- Center Logo -->
        <div class="logo-wrapper">
          <div class="logo-bg"></div>
          <div class="logo-ring"></div>
          <div class="logo-text">YA</div>
        </div>

        <!-- Brand Name -->
        <div class="brand-name">
          <span class="letter" style="--delay: 0">Y</span>
          <span class="letter" style="--delay: 1">o</span>
          <span class="letter" style="--delay: 2">u</span>
          <span class="letter" style="--delay: 3">t</span>
          <span class="letter" style="--delay: 4">h</span>
          <span class="letter space" style="--delay: 5">&nbsp;</span>
          <span class="letter" style="--delay: 6">A</span>
          <span class="letter" style="--delay: 7">l</span>
          <span class="letter" style="--delay: 8">l</span>
          <span class="letter" style="--delay: 9">y</span>
        </div>

        <!-- Loading Bar -->
        <div class="loading-bar">
          <div class="loading-progress"></div>
        </div>

        <!-- Tagline -->
        <div class="tagline">Empowering Tomorrow's Leaders</div>
      </div>

      <!-- Corner Accents -->
      <div class="corner corner-tl"></div>
      <div class="corner corner-tr"></div>
      <div class="corner corner-bl"></div>
      <div class="corner corner-br"></div>
    </div>
  </Transition>
</template>

<script setup>
const { defineProps } = require("vue");
defineProps({
  loading: {
    type: Boolean,
    required: true,
  },
});
</script>

<style scoped>
.preloader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overflow: hidden;
}

/* Background Shapes */
.bg-shapes {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.shape {
  position: absolute;
  opacity: 0.08;
}

.circle {
  border-radius: 50%;
  background: var(--logo-color, #302e3c);
}

.c1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -100px;
  animation: float 20s ease-in-out infinite;
}

.c2 {
  width: 200px;
  height: 200px;
  bottom: -50px;
  left: -50px;
  animation: float 15s ease-in-out infinite reverse;
}

.c3 {
  width: 100px;
  height: 100px;
  top: 20%;
  left: 10%;
  animation: float 12s ease-in-out infinite;
  animation-delay: -3s;
}

.c4 {
  width: 150px;
  height: 150px;
  bottom: 20%;
  right: 10%;
  animation: float 18s ease-in-out infinite;
  animation-delay: -5s;
}

.square {
  background: var(--logo-color, #302e3c);
  transform: rotate(45deg);
}

.s1 {
  width: 80px;
  height: 80px;
  top: 15%;
  right: 20%;
  animation: spin-float 25s linear infinite;
}

.s2 {
  width: 60px;
  height: 60px;
  bottom: 25%;
  left: 15%;
  animation: spin-float 20s linear infinite reverse;
}

.triangle {
  width: 0;
  height: 0;
  border-left: 40px solid transparent;
  border-right: 40px solid transparent;
  border-bottom: 70px solid var(--logo-color, #302e3c);
}

.t1 {
  top: 30%;
  right: 8%;
  animation: float 14s ease-in-out infinite;
  animation-delay: -2s;
}

.t2 {
  bottom: 15%;
  left: 25%;
  animation: float 16s ease-in-out infinite;
  animation-delay: -4s;
  transform: rotate(180deg);
}

/* Main Loader */
.loader-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  z-index: 2;
}

/* Orbiting Rings */
.orbit-container {
  position: relative;
  width: 200px;
  height: 200px;
}

.orbit {
  position: absolute;
  top: 50%;
  left: 50%;
  border: 2px solid transparent;
  border-top-color: var(--logo-color, #302e3c);
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.orbit-1 {
  width: 180px;
  height: 180px;
  animation: orbit 3s linear infinite;
  opacity: 0.3;
}

.orbit-2 {
  width: 150px;
  height: 150px;
  animation: orbit 2.5s linear infinite reverse;
  opacity: 0.5;
}

.orbit-3 {
  width: 120px;
  height: 120px;
  animation: orbit 2s linear infinite;
  opacity: 0.7;
}

.orbit-dot {
  position: absolute;
  width: 10px;
  height: 10px;
  background: var(--logo-color, #0b3c8a);
  border-radius: 50%;
  top: -5px;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 0 10px var(--logo-color, #0b3c8a);
}

/* Center Logo */
.logo-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-bg {
  position: absolute;
  inset: 0;
  background: var(--logo-color, #0b3c8a);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

.logo-ring {
  position: absolute;
  inset: -10px;
  border: 3px solid var(--logo-color, #0b3c8a);
  border-radius: 50%;
  opacity: 0.3;
  animation: ring-pulse 2s ease-in-out infinite;
}

.logo-text {
  position: relative;
  font-size: 2.5rem;
  font-weight: 900;
  color: white;
  letter-spacing: -0.05em;
  z-index: 1;
}

/* Brand Name */
.brand-name {
  display: flex;
  gap: 0.1em;
  margin-top: 1rem;
}

.letter {
  font-size: 2rem;
  font-weight: 800;
  color: var(--logo-color, #0b3c8a);
  opacity: 0;
  transform: translateY(20px);
  animation: letter-appear 0.5s ease forwards;
  animation-delay: calc(var(--delay) * 0.1s + 0.5s);
}

.letter.space {
  width: 0.3em;
}

/* Loading Bar */
.loading-bar {
  width: 200px;
  height: 4px;
  background: rgba(48, 46, 60, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.5rem;
}

.loading-progress {
  height: 100%;
  background: var(--logo-color, #0b3c8a);
  border-radius: 2px;
  animation: loading 2s ease-in-out infinite;
}

/* Tagline */
.tagline {
  font-size: 0.85rem;
  color: var(--logo-color, #0b3c8a);
  opacity: 0.6;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  animation: fade-in 1s ease forwards;
  animation-delay: 1.5s;
  opacity: 0;
}

/* Corner Accents */
.corner {
  position: absolute;
  width: 60px;
  height: 60px;
  border: 3px solid var(--logo-color, #0b3c8a);
  opacity: 0.1;
}

.corner-tl {
  top: 30px;
  left: 30px;
  border-right: none;
  border-bottom: none;
  animation: corner-fade 3s ease-in-out infinite;
}

.corner-tr {
  top: 30px;
  right: 30px;
  border-left: none;
  border-bottom: none;
  animation: corner-fade 3s ease-in-out infinite;
  animation-delay: 0.5s;
}

.corner-bl {
  bottom: 30px;
  left: 30px;
  border-right: none;
  border-top: none;
  animation: corner-fade 3s ease-in-out infinite;
  animation-delay: 1s;
}

.corner-br {
  bottom: 30px;
  right: 30px;
  border-left: none;
  border-top: none;
  animation: corner-fade 3s ease-in-out infinite;
  animation-delay: 1.5s;
}

/* Animations */
@keyframes float {
  0%,
  100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(10px, -20px);
  }
  50% {
    transform: translate(-10px, 10px);
  }
  75% {
    transform: translate(20px, 5px);
  }
}

@keyframes spin-float {
  0% {
    transform: rotate(45deg) translate(0, 0);
  }
  25% {
    transform: rotate(135deg) translate(10px, -10px);
  }
  50% {
    transform: rotate(225deg) translate(-5px, 5px);
  }
  75% {
    transform: rotate(315deg) translate(5px, 10px);
  }
  100% {
    transform: rotate(405deg) translate(0, 0);
  }
}

@keyframes orbit {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(48, 46, 60, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 30px 10px rgba(48, 46, 60, 0.1);
  }
}

@keyframes ring-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.1;
  }
}

@keyframes letter-appear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes loading {
  0% {
    width: 0%;
    margin-left: 0;
  }
  50% {
    width: 100%;
    margin-left: 0;
  }
  100% {
    width: 0%;
    margin-left: 100%;
  }
}

@keyframes fade-in {
  to {
    opacity: 0.6;
  }
}

@keyframes corner-fade {
  0%,
  100% {
    opacity: 0.1;
  }
  50% {
    opacity: 0.25;
  }
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .orbit-container {
    width: 160px;
    height: 160px;
  }

  .orbit-1 {
    width: 150px;
    height: 150px;
  }

  .orbit-2 {
    width: 125px;
    height: 125px;
  }

  .orbit-3 {
    width: 100px;
    height: 100px;
  }

  .logo-wrapper {
    width: 80px;
    height: 80px;
  }

  .logo-text {
    font-size: 2rem;
  }

  .letter {
    font-size: 1.6rem;
  }

  .loading-bar {
    width: 160px;
  }

  .tagline {
    font-size: 0.7rem;
  }

  .corner {
    width: 40px;
    height: 40px;
  }

  .corner-tl,
  .corner-tr,
  .corner-bl,
  .corner-br {
    top: 20px;
    left: 20px;
  }

  .corner-tr {
    left: auto;
    right: 20px;
  }

  .corner-bl {
    top: auto;
    bottom: 20px;
  }

  .corner-br {
    top: auto;
    left: auto;
    bottom: 20px;
    right: 20px;
  }

  .c1 {
    width: 200px;
    height: 200px;
  }

  .c2 {
    width: 150px;
    height: 150px;
  }

  .c3,
  .c4 {
    display: none;
  }

  .s1,
  .s2 {
    width: 50px;
    height: 50px;
  }

  .triangle {
    border-left-width: 25px;
    border-right-width: 25px;
    border-bottom-width: 45px;
  }
}

@media (max-width: 480px) {
  .orbit-container {
    width: 140px;
    height: 140px;
  }

  .orbit-1 {
    width: 130px;
    height: 130px;
  }

  .orbit-2 {
    width: 105px;
    height: 105px;
  }

  .orbit-3 {
    width: 80px;
    height: 80px;
  }

  .orbit-dot {
    width: 8px;
    height: 8px;
    top: -4px;
  }

  .logo-wrapper {
    width: 65px;
    height: 65px;
  }

  .logo-ring {
    inset: -8px;
  }

  .logo-text {
    font-size: 1.6rem;
  }

  .letter {
    font-size: 1.3rem;
  }

  .loading-bar {
    width: 140px;
    height: 3px;
  }

  .tagline {
    font-size: 0.6rem;
    letter-spacing: 0.1em;
  }

  .s1,
  .s2,
  .t1,
  .t2 {
    display: none;
  }
}
</style>
